import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

// Cart localStorage key scoped per restaurant slug
const getCartKey = (slug) => `cafe_cart_${slug || 'default'}`;

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD': {
      // If cart has items from a different restaurant, clear first
      const cartSlug = state.slug;
      const itemSlug = action.item.slug;
      if (cartSlug && itemSlug && cartSlug !== itemSlug && state.items.length > 0) {
        // Different restaurant — start fresh
        return { slug: itemSlug, items: [{ ...action.item, quantity: 1 }] };
      }
      const existing = state.items.find(i => i.id === action.item.id);
      if (existing) {
        return {
          ...state,
          slug: itemSlug || cartSlug,
          items: state.items.map(i =>
            i.id === action.item.id ? { ...i, quantity: i.quantity + 1 } : i
          )
        };
      }
      return { slug: itemSlug || cartSlug, items: [...state.items, { ...action.item, quantity: 1 }] };
    }
    case 'REMOVE':
      return { ...state, items: state.items.filter(i => i.id !== action.id) };
    case 'UPDATE_QTY':
      if (action.qty <= 0) {
        return { ...state, items: state.items.filter(i => i.id !== action.id) };
      }
      return {
        ...state,
        items: state.items.map(i => i.id === action.id ? { ...i, quantity: action.qty } : i)
      };
    case 'CLEAR':
      return { ...state, items: [] };
    case 'LOAD_FOR_SLUG':
      return { slug: action.slug, items: action.items };
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  // Get slug from URL on mount
  const getSlugFromUrl = () => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    // Skip known non-restaurant paths
    const skip = ['super-admin', 'admin', 'staff'];
    if (parts.length > 0 && !skip.includes(parts[0])) return parts[0];
    return 'default';
  };

  const initialSlug = getSlugFromUrl();
  const initialKey = getCartKey(initialSlug);

  const [state, dispatch] = useReducer(cartReducer, {
    slug: initialSlug,
    items: (() => {
      try { return JSON.parse(localStorage.getItem(initialKey) || '[]'); }
      catch { return []; }
    })()
  });

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    const key = getCartKey(state.slug);
    localStorage.setItem(key, JSON.stringify(state.items));
  }, [state.items, state.slug]);

  // Load correct cart when slug changes (e.g. user navigates to a different restaurant)
  const loadCartForSlug = useCallback((slug) => {
    try {
      const items = JSON.parse(localStorage.getItem(getCartKey(slug)) || '[]');
      dispatch({ type: 'LOAD_FOR_SLUG', slug, items });
    } catch {
      dispatch({ type: 'LOAD_FOR_SLUG', slug, items: [] });
    }
  }, []);

  const total = state.items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const count = state.items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items: state.items,
      total,
      count,
      slug: state.slug,
      loadCartForSlug,
      addItem:    (item) => dispatch({ type: 'ADD', item }),
      removeItem: (id)   => dispatch({ type: 'REMOVE', id }),
      updateQty:  (id, qty) => dispatch({ type: 'UPDATE_QTY', id, qty }),
      clearCart:  () => dispatch({ type: 'CLEAR' }),
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
